/* eslint-disable react/no-unstable-nested-components */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    Activity,
    BarChart3,
    Building2,
    Calendar,
    ChevronRight,
    Download,
    Layers,
    LogOut,
    PieChart as PieIcon,
    UserRound,
    Users,
} from "lucide-react";
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { adminApi, adminLogout } from "@/lib/adminApi";
import Logo from "@/components/site/Logo";
import ContentManager from "@/pages/admin/ContentManager";
import PricingManager from "@/pages/admin/PricingManager";
import PageContentManager from "@/pages/admin/PageContentManager";
import TestimonialsManager from "@/pages/admin/TestimonialsManager";

const COLORS = ["#08C020", "#1A2E4F", "#C9A646", "#2A416F", "#1ED639", "#DCC271", "#0F1D33"];

const InsightsCenter = () => {
    const navigate = useNavigate();
    const [summary, setSummary] = useState(null);
    const [snapshots, setSnapshots] = useState([]);
    const [foundations, setFoundations] = useState([]);
    const [workshops, setWorkshops] = useState([]);
    const [discovery, setDiscovery] = useState([]);
    const [contact, setContact] = useState([]);
    const [distrib, setDistrib] = useState(null);
    const [tab, setTab] = useState("overview");

    useEffect(() => {
        const load = async () => {
            try {
                const [s, sn, f, w, d, c, dist] = await Promise.all([
                    adminApi.get("/admin/dashboard/summary"),
                    adminApi.get("/admin/snapshots"),
                    adminApi.get("/admin/foundations"),
                    adminApi.get("/admin/workshops"),
                    adminApi.get("/admin/discovery"),
                    adminApi.get("/admin/contact"),
                    adminApi.get("/admin/reports/distributions"),
                ]);
                setSummary(s.data);
                setSnapshots(sn.data);
                setFoundations(f.data);
                setWorkshops(w.data);
                setDiscovery(d.data);
                setContact(c.data);
                setDistrib(dist.data);
            } catch (e) {
                // 401 redirect handled by axios interceptor
            }
        };
        load();
    }, []);

    const logout = async () => {
        await adminLogout();
        navigate("/admin/login");
    };

    const directory = useMemo(() => {
        // Unique orgs from snapshots (lead set used for org directory)
        const seen = new Map();
        for (const s of snapshots) {
            const key = `${s.lead.organization_name}|${s.lead.email}`;
            if (!seen.has(key)) seen.set(key, { ...s.lead, latest: s });
        }
        return Array.from(seen.values());
    }, [snapshots]);

    const pipeline = summary?.pipeline || {
        snapshot_completed: 0,
        registered: 0,
        discovery_call: 0,
        workshop_completed: 0,
    };

    return (
        <div data-testid="insights-page" className="min-h-screen bg-surface">
            {/* Topbar */}
            <header className="bg-white border-b border-surface-border/60">
                <div className="ih-container h-20 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Logo />
                        <span className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-green_brand/10">
                            <span className="w-1.5 h-1.5 rounded-full bg-green_brand" />
                            <span className="font-poppins text-[10px] uppercase tracking-[0.18em] text-green_brand">Insights Center</span>
                        </span>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/" className="ih-btn-ghost hidden sm:inline-flex">View site <ChevronRight size={14} /></Link>
                        <button onClick={logout} data-testid="admin-logout" className="ih-btn-secondary !py-2 !px-4 text-xs">
                            <LogOut size={14} /> Sign out
                        </button>
                    </div>
                </div>
            </header>

            <div className="ih-container py-10">
                <p className="ih-eyebrow">Executive Summary</p>
                <h1 className="mt-2 font-montserrat font-bold text-indigo_brand text-3xl sm:text-4xl tracking-tight">
                    The Intuitus Hub™ — Activity at a glance
                </h1>

                {/* KPI cards */}
                <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <KpiCard
                        icon={Activity}
                        label="New Snapshot Completions"
                        value={summary?.totals?.new_snapshot_completions ?? "—"}
                        testid="kpi-snapshots"
                    />
                    <KpiCard
                        icon={Building2}
                        label="New Organizations"
                        value={summary?.totals?.new_organizations ?? "—"}
                        testid="kpi-organizations"
                    />
                    <KpiCard
                        icon={Layers}
                        label="Foundations Registrations"
                        value={summary?.totals?.foundations_registrations ?? "—"}
                        testid="kpi-foundations"
                    />
                    <KpiCard
                        icon={Calendar}
                        label="Discovery Calls"
                        value={summary?.totals?.discovery_calls ?? "—"}
                        testid="kpi-discovery"
                    />
                </div>

                {/* Pipeline */}
                <div className="mt-10 ih-card p-6 sm:p-8" data-testid="pipeline-card">
                    <div className="flex items-center justify-between mb-5">
                        <div>
                            <p className="ih-eyebrow">Consulting Pipeline</p>
                            <h2 className="mt-1 font-montserrat font-bold text-indigo_brand text-xl">
                                Where each lead sits today
                            </h2>
                        </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        {[
                            { label: "Snapshot Completed", value: pipeline.snapshot_completed },
                            { label: "Registered", value: pipeline.registered },
                            { label: "Discovery Call", value: pipeline.discovery_call },
                            { label: "Workshop Completed", value: pipeline.workshop_completed },
                        ].map((s, i) => (
                            <div
                                key={s.label}
                                data-testid={`pipeline-stage-${i + 1}`}
                                className="relative rounded-xl border border-surface-border/60 p-5 bg-white"
                            >
                                <span className="absolute top-0 left-0 h-[3px] w-10 bg-green_brand" />
                                <p className="font-poppins text-[10px] uppercase tracking-[0.18em] text-[#4A5568]">
                                    Stage {i + 1}
                                </p>
                                <p className="mt-2 font-raleway font-semibold text-indigo_brand text-sm">{s.label}</p>
                                <p className="mt-3 font-montserrat font-bold text-indigo_brand text-3xl">{s.value}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Tabs */}
                <Tabs value={tab} onValueChange={setTab} className="mt-10">
                    <TabsList className="bg-white border border-surface-border/60 p-1 rounded-lg h-auto flex-wrap" data-testid="insights-tabs">
                        <TabsTrigger value="overview" data-testid="tab-overview" className="data-[state=active]:bg-indigo_brand data-[state=active]:text-white px-4 py-2 text-xs font-poppins font-medium uppercase tracking-[0.12em]">Reports</TabsTrigger>
                        <TabsTrigger value="organizations" data-testid="tab-organizations" className="data-[state=active]:bg-indigo_brand data-[state=active]:text-white px-4 py-2 text-xs font-poppins font-medium uppercase tracking-[0.12em]">Organizations</TabsTrigger>
                        <TabsTrigger value="assessments" data-testid="tab-assessments" className="data-[state=active]:bg-indigo_brand data-[state=active]:text-white px-4 py-2 text-xs font-poppins font-medium uppercase tracking-[0.12em]">Assessments</TabsTrigger>
                        <TabsTrigger value="foundations" data-testid="tab-foundations" className="data-[state=active]:bg-indigo_brand data-[state=active]:text-white px-4 py-2 text-xs font-poppins font-medium uppercase tracking-[0.12em]">Foundations</TabsTrigger>
                        <TabsTrigger value="workshops" data-testid="tab-workshops" className="data-[state=active]:bg-indigo_brand data-[state=active]:text-white px-4 py-2 text-xs font-poppins font-medium uppercase tracking-[0.12em]">Workshops</TabsTrigger>
                        <TabsTrigger value="discovery" data-testid="tab-discovery" className="data-[state=active]:bg-indigo_brand data-[state=active]:text-white px-4 py-2 text-xs font-poppins font-medium uppercase tracking-[0.12em]">Discovery</TabsTrigger>
                        <TabsTrigger value="contact" data-testid="tab-contact" className="data-[state=active]:bg-indigo_brand data-[state=active]:text-white px-4 py-2 text-xs font-poppins font-medium uppercase tracking-[0.12em]">Contact</TabsTrigger>
                        <TabsTrigger value="content" data-testid="tab-content" className="data-[state=active]:bg-indigo_brand data-[state=active]:text-white px-4 py-2 text-xs font-poppins font-medium uppercase tracking-[0.12em]">Content</TabsTrigger>
                        <TabsTrigger value="pricing" data-testid="tab-pricing" className="data-[state=active]:bg-indigo_brand data-[state=active]:text-white px-4 py-2 text-xs font-poppins font-medium uppercase tracking-[0.12em]">Pricing</TabsTrigger>
                        <TabsTrigger value="page-content" data-testid="tab-page-content" className="data-[state=active]:bg-indigo_brand data-[state=active]:text-white px-4 py-2 text-xs font-poppins font-medium uppercase tracking-[0.12em]">Site Copy</TabsTrigger>
                        <TabsTrigger value="testimonials" data-testid="tab-testimonials" className="data-[state=active]:bg-indigo_brand data-[state=active]:text-white px-4 py-2 text-xs font-poppins font-medium uppercase tracking-[0.12em]">Testimonials</TabsTrigger>
                    </TabsList>

                    {/* REPORTS */}
                    <TabsContent value="overview" className="mt-6">
                        <div className="grid gap-6 lg:grid-cols-2">
                            <ChartCard icon={PieIcon} title="Industry Breakdown" testid="report-industry">
                                <ResponsiveContainer width="100%" height={260}>
                                    <PieChart>
                                        <Pie data={distrib?.by_industry || []} dataKey="value" nameKey="label" cx="50%" cy="50%" outerRadius={90}>
                                            {(distrib?.by_industry || []).map((_, i) => (
                                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                        <Legend wrapperStyle={{ fontSize: 11 }} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </ChartCard>
                            <ChartCard icon={BarChart3} title="Organization Size" testid="report-org-size">
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={distrib?.by_organization_size || []}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#08C020" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartCard>
                            <ChartCard icon={Activity} title="Snapshot Activity" testid="report-snapshot-activity">
                                <ResponsiveContainer width="100%" height={260}>
                                    <LineChart data={distrib?.snapshot_activity || []}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Line type="monotone" dataKey="value" stroke="#1A2E4F" strokeWidth={2.5} dot={{ r: 3, fill: "#08C020" }} />
                                    </LineChart>
                                </ResponsiveContainer>
                            </ChartCard>
                            <ChartCard icon={BarChart3} title="Readiness Bands" testid="report-bands">
                                <ResponsiveContainer width="100%" height={260}>
                                    <BarChart data={distrib?.by_band || []}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                                        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                        <Tooltip />
                                        <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#C9A646" />
                                    </BarChart>
                                </ResponsiveContainer>
                            </ChartCard>
                        </div>
                    </TabsContent>

                    {/* ORGANIZATIONS */}
                    <TabsContent value="organizations" className="mt-6">
                        <DataTableCard
                            title="Organization Directory"
                            count={directory.length}
                            testid="organizations-table"
                            empty="No organizations yet."
                            data={directory}
                            columns={[
                                { h: "Organization", c: (r) => r.organization_name },
                                { h: "Contact", c: (r) => `${r.first_name} ${r.last_name}` },
                                { h: "Email", c: (r) => r.email },
                                { h: "Job Title", c: (r) => r.job_title },
                                { h: "Industry", c: (r) => r.industry },
                                { h: "Size", c: (r) => r.organization_size },
                            ]}
                        />
                    </TabsContent>

                    {/* ASSESSMENTS */}
                    <TabsContent value="assessments" className="mt-6">
                        <DataTableCard
                            title="Assessment Results"
                            count={snapshots.length}
                            testid="assessments-table"
                            empty="No assessments yet."
                            data={snapshots}
                            columns={[
                                { h: "Organization", c: (r) => r.lead.organization_name },
                                { h: "Contact", c: (r) => `${r.lead.first_name} ${r.lead.last_name}` },
                                {
                                    h: "Overall",
                                    c: (r) => (
                                        <span className="font-montserrat font-bold text-indigo_brand">{Math.round(r.overall)}</span>
                                    ),
                                },
                                {
                                    h: "Band",
                                    c: (r) => <Badge variant="outline" className="border-green_brand text-green_brand">{r.band.label}</Badge>,
                                },
                                {
                                    h: "Pillar Scores",
                                    c: (r) => (
                                        <div className="flex gap-1.5">
                                            {r.pillar_scores.map((p) => (
                                                <span
                                                    key={p.slug}
                                                    title={`${p.name}: ${Math.round(p.score)}`}
                                                    className="inline-flex w-7 h-7 rounded items-center justify-center text-[10px] font-poppins font-semibold text-white"
                                                    style={{ background: shadeForScore(p.score) }}
                                                >
                                                    {Math.round(p.score)}
                                                </span>
                                            ))}
                                        </div>
                                    ),
                                },
                                { h: "Completed", c: (r) => formatDate(r.completed_at) },
                                {
                                    h: "",
                                    c: (r) => (
                                        <a
                                            href={`${process.env.REACT_APP_BACKEND_URL}/api/snapshot/${r.id}/pdf`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="ih-btn-ghost text-xs"
                                            data-testid={`assess-pdf-${r.id}`}
                                        >
                                            <Download size={13} /> PDF
                                        </a>
                                    ),
                                },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="foundations" className="mt-6">
                        <DataTableCard
                            title="Foundations Registrations"
                            count={foundations.length}
                            testid="foundations-table"
                            empty="No Foundations registrations yet."
                            data={foundations}
                            columns={[
                                { h: "Organization", c: (r) => r.organization_name },
                                { h: "Contact", c: (r) => `${r.first_name} ${r.last_name}` },
                                { h: "Email", c: (r) => r.email },
                                { h: "Tier", c: (r) => r.tier_key || "regular" },
                                { h: "Industry", c: (r) => r.industry },
                                { h: "Size", c: (r) => r.organization_size },
                                { h: "Payment", c: (r) => <PaidBadge paid={r.paid} amount={r.amount_paid} /> },
                                { h: "Created", c: (r) => formatDate(r.created_at) },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="workshops" className="mt-6">
                        <DataTableCard
                            title="Snapshot Workshop Registrations"
                            count={workshops.length}
                            testid="workshops-table"
                            empty="No Workshop registrations yet."
                            data={workshops}
                            columns={[
                                { h: "Contact", c: (r) => `${r.first_name} ${r.last_name}` },
                                { h: "Email", c: (r) => r.email },
                                { h: "Organization", c: (r) => r.organization_name || "—" },
                                { h: "Tier", c: (r) => r.tier_key || "individual" },
                                { h: "Payment", c: (r) => <PaidBadge paid={r.paid} amount={r.amount_paid} /> },
                                { h: "Created", c: (r) => formatDate(r.created_at) },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="discovery" className="mt-6">
                        <DataTableCard
                            title="Discovery Call Requests"
                            count={discovery.length}
                            testid="discovery-table"
                            empty="No Discovery Call requests yet."
                            data={discovery}
                            columns={[
                                { h: "Contact", c: (r) => `${r.first_name} ${r.last_name}` },
                                { h: "Email", c: (r) => r.email },
                                { h: "Organization", c: (r) => r.organization_name || "—" },
                                { h: "Preferred Date", c: (r) => r.preferred_date || "—" },
                                { h: "Preferred Time", c: (r) => r.preferred_time || "—" },
                                { h: "Payment", c: (r) => <PaidBadge paid={r.paid} amount={r.amount_paid} /> },
                                { h: "Requested", c: (r) => formatDate(r.created_at) },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="contact" className="mt-6">
                        <DataTableCard
                            title="Contact Messages"
                            count={contact.length}
                            testid="contact-table"
                            empty="No contact messages yet."
                            data={contact}
                            columns={[
                                { h: "Name", c: (r) => r.name },
                                { h: "Email", c: (r) => r.email },
                                { h: "Organization", c: (r) => r.organization_name || "—" },
                                {
                                    h: "Message",
                                    c: (r) => (
                                        <p className="max-w-md font-opensans text-sm text-[#4A5568] line-clamp-2">{r.message}</p>
                                    ),
                                },
                                { h: "Received", c: (r) => formatDate(r.created_at) },
                            ]}
                        />
                    </TabsContent>

                    <TabsContent value="content" className="mt-6">
                        <ContentManager />
                    </TabsContent>

                    <TabsContent value="pricing" className="mt-6">
                        <PricingManager />
                    </TabsContent>

                    <TabsContent value="page-content" className="mt-6">
                        <PageContentManager />
                    </TabsContent>

                    <TabsContent value="testimonials" className="mt-6">
                        <TestimonialsManager />
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
};

// ----- Subcomponents -----

const KpiCard = ({ icon: Icon, label, value, testid }) => (
    <div data-testid={testid} className="ih-card p-6 relative overflow-hidden">
        <span className="absolute top-0 left-0 h-[3px] w-12 bg-green_brand" />
        <div className="flex items-center gap-3">
            <span className="inline-flex w-10 h-10 items-center justify-center rounded-lg bg-indigo_brand/8">
                <Icon size={18} className="text-indigo_brand" />
            </span>
            <p className="font-poppins text-[10px] uppercase tracking-[0.18em] text-[#4A5568]">{label}</p>
        </div>
        <p className="mt-4 font-montserrat font-bold text-indigo_brand text-4xl tabular-nums">{value}</p>
    </div>
);

const ChartCard = ({ icon: Icon, title, children, testid }) => (
    <div data-testid={testid} className="ih-card p-6">
        <div className="flex items-center gap-2 mb-4">
            <Icon size={16} className="text-green_brand" />
            <p className="font-raleway font-semibold text-indigo_brand text-sm uppercase tracking-[0.14em]">{title}</p>
        </div>
        {children}
    </div>
);

const DataTableCard = ({ title, count, columns, data, empty, testid }) => (
    <div className="ih-card p-6 sm:p-8" data-testid={testid}>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-5">
            <div>
                <p className="ih-eyebrow">Records</p>
                <h2 className="mt-1 font-montserrat font-bold text-indigo_brand text-xl">{title}</h2>
            </div>
            <Badge variant="outline" className="border-indigo_brand/30 text-indigo_brand font-poppins text-[11px]">
                {count} total
            </Badge>
        </div>
        {data.length === 0 ? (
            <div className="text-center py-12 font-opensans text-sm text-[#4A5568]">{empty}</div>
        ) : (
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow className="border-surface-border/60">
                            {columns.map((c) => (
                                <TableHead key={c.h} className="font-raleway font-semibold text-indigo_brand text-xs uppercase tracking-[0.12em]">
                                    {c.h}
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {data.map((row, i) => (
                            <TableRow key={row.id || i} className="border-surface-border/40">
                                {columns.map((c, j) => (
                                    <TableCell key={j} className="font-opensans text-sm text-indigo_brand py-3">
                                        {c.c(row)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        )}
    </div>
);

const shadeForScore = (score) => {
    if (score >= 85) return "#08C020";
    if (score >= 70) return "#1ED639";
    if (score >= 50) return "#C9A646";
    return "#1A2E4F";
};

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch {
        return iso;
    }
};

const PaidBadge = ({ paid, amount }) => {
    if (paid) {
        return (
            <span
                data-testid="paid-badge"
                className="inline-flex items-center gap-1.5 rounded-full bg-green_brand/15 text-green_brand px-2.5 py-1 font-poppins text-[10px] font-semibold uppercase tracking-[0.14em]"
            >
                <span className="w-1.5 h-1.5 rounded-full bg-green_brand" />
                Paid{amount ? ` · $${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : ""}
            </span>
        );
    }
    return (
        <span
            data-testid="unpaid-badge"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#F5F5F5] text-[#4A5568] px-2.5 py-1 font-poppins text-[10px] font-semibold uppercase tracking-[0.14em]"
        >
            <span className="w-1.5 h-1.5 rounded-full bg-[#B33A3A]" />
            Unpaid
        </span>
    );
};

export default InsightsCenter;
